import { downloadQrCard } from "../lib/qrCardGenerator";
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Building2,
  Plus,
  Search,
  QrCode,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  MapPin,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const Clinics = () => {
  const { user, token } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [filteredClinics, setFilteredClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [floorFilter, setFloorFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [regeneratingQr, setRegeneratingQr] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    floor: "",
    building: "المبنى الرئيسي",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchClinics();
  }, [token]);

  useEffect(() => {
    filterClinics();
  }, [clinics, searchQuery, floorFilter]);

  const fetchClinics = async () => {
    try {
      const response = await axios.get(`${API}/clinics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClinics(response.data);
    } catch (error) {
      toast.error("فشل في تحميل العيادات");
    } finally {
      setLoading(false);
    }
  };

  const filterClinics = () => {
    let filtered = [...clinics];
    
    if (searchQuery) {
      filtered = filtered.filter(clinic =>
        clinic.name.includes(searchQuery) ||
        clinic.floor.includes(searchQuery) ||
        clinic.building.includes(searchQuery)
      );
    }
    
    if (floorFilter !== "all") {
      filtered = filtered.filter(clinic => clinic.floor === floorFilter);
    }
    
    setFilteredClinics(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.floor) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setSubmitting(true);
    try {
      if (selectedClinic) {
        await axios.put(`${API}/clinics/${selectedClinic.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("تم تحديث العيادة بنجاح");
      } else {
        await axios.post(`${API}/clinics`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("تم إضافة العيادة بنجاح");
      }
      fetchClinics();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (clinicId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه العيادة؟")) return;
    
    try {
      await axios.delete(`${API}/clinics/${clinicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم حذف العيادة بنجاح");
      fetchClinics();
    } catch (error) {
      toast.error("فشل في حذف العيادة");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", floor: "", building: "المبنى الرئيسي", notes: "" });
    setSelectedClinic(null);
  };

  const openEditDialog = (clinic) => {
    setSelectedClinic(clinic);
    setFormData({
      name: clinic.name,
      floor: clinic.floor,
      building: clinic.building,
      notes: clinic.notes
    });
    setDialogOpen(true);
  };

  const openQRDialog = (clinic) => {
    setSelectedClinic(clinic);
    setQrDialogOpen(true);
  };

  const handleRegenerateSingleQr = async () => {
    if (!selectedClinic) return;
    setRegeneratingQr(true);
    try {
      const res = await axios.post(
        `${API}/location/clinic/${selectedClinic.id}/regenerate-qr?base_url=${encodeURIComponent(window.location.origin)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedClinic({ ...selectedClinic, qr_code: res.data.qr_code });
      toast.success("تم إعادة توليد رمز QR بنجاح");
      fetchClinics();
    } catch {
      toast.error("فشل إعادة توليد رمز QR");
    } finally {
      setRegeneratingQr(false);
    }
  };

  const floors = [...new Set(clinics.map(c => c.floor))].sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clinics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">العيادات</h1>
          <p className="text-slate-500 mt-1">إدارة عيادات المركز ({clinics.length} عيادة)</p>
        </div>
        {user?.role === "admin" && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="add-clinic-btn">
                <Plus className="w-4 h-4 ml-2" />
                إضافة عيادة
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" dir="rtl">
              <DialogHeader>
                <DialogTitle className="text-[#655dc6]">
                  {selectedClinic ? "تعديل العيادة" : "إضافة عيادة جديدة"}
                </DialogTitle>
                <DialogDescription>
                  أدخل بيانات العيادة
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>اسم العيادة *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="مثال: عيادة 1"
                    className="text-right"
                    data-testid="clinic-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>الطابق *</Label>
                  <Input
                    value={formData.floor}
                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                    placeholder="مثال: الطابق 1"
                    className="text-right"
                    data-testid="clinic-floor-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>المبنى</Label>
                  <Input
                    value={formData.building}
                    onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                    placeholder="مثال: المبنى الرئيسي"
                    className="text-right"
                    data-testid="clinic-building-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ملاحظات</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="ملاحظات إضافية"
                    className="text-right"
                    data-testid="clinic-notes-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                    disabled={submitting}
                    data-testid="submit-clinic-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedClinic ? "تحديث" : "إضافة"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1"
                  >
                    إلغاء
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="البحث في العيادات..."
                className="pr-10 text-right"
                data-testid="search-clinics-input"
              />
            </div>
            <Select value={floorFilter} onValueChange={setFloorFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="floor-filter">
                <SelectValue placeholder="جميع الطوابق" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الطوابق</SelectItem>
                {floors.map(floor => (
                  <SelectItem key={floor} value={floor}>{floor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Clinics Grid */}
      {filteredClinics.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredClinics.map((clinic) => (
            <Card
              key={clinic.id}
              className="card-hover border-0 shadow-sm group"
              data-testid={`clinic-card-${clinic.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F5F3F9] flex items-center justify-center group-hover:bg-[#655dc6] transition-colors">
                    <Building2 className="w-6 h-6 text-[#655dc6] group-hover:text-white transition-colors" />
                  </div>
                  <button
                    onClick={() => openQRDialog(clinic)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    title="عرض رمز QR"
                    data-testid={`qr-btn-${clinic.id}`}
                  >
                    <QrCode className="w-5 h-5 text-[#9b8fd4]" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg text-[#655dc6]">{clinic.name}</h3>
                  {clinic.open_requests_count > 0 && (
                    <Badge 
                      className="bg-amber-100 text-amber-700 hover:bg-amber-100 flex items-center gap-1"
                      data-testid={`open-requests-badge-${clinic.id}`}
                    >
                      <AlertCircle className="w-3 h-3" />
                      {clinic.open_requests_count}
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <MapPin className="w-4 h-4" />
                  <span>{clinic.floor}</span>
                  {clinic.building && <span>• {clinic.building}</span>}
                </div>

                <div className="flex gap-2">
                  <Link to={`/clinics/${clinic.id}`} className="flex-1">
                    <Button variant="outline" className="w-full" size="sm" data-testid={`view-clinic-${clinic.id}`}>
                      <Eye className="w-4 h-4 ml-1" />
                      عرض
                    </Button>
                  </Link>
                  {user?.role === "admin" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(clinic)}
                        data-testid={`edit-clinic-${clinic.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(clinic.id)}
                        data-testid={`delete-clinic-${clinic.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-[#655dc6] mb-2">لا توجد عيادات</h3>
            <p className="text-slate-500">
              {searchQuery || floorFilter !== "all" ? "لم يتم العثور على نتائج مطابقة" : "لم يتم إضافة عيادات بعد"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">رمز QR - {selectedClinic?.name}</DialogTitle>
            <DialogDescription>
              امسح هذا الرمز للإبلاغ عن مشكلة صيانة
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            {selectedClinic?.qr_code && (
              <div className="qr-code-container">
                <img
                  src={`data:image/png;base64,${selectedClinic.qr_code}`}
                  alt={`QR Code for ${selectedClinic.name}`}
                  className="w-48 h-48"
                />
              </div>
            )}
            <p className="mt-4 text-center text-slate-500 text-sm">
              {selectedClinic?.floor} • {selectedClinic?.building}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                await downloadQrCard({
                  qrBase64: selectedClinic.qr_code,
                  name: selectedClinic.name,
                  floor: selectedClinic.floor,
                  building: selectedClinic.building
                });
              }}
              className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
            >
              تحميل رمز QR
            </Button>
            <Button
              variant="outline"
              onClick={handleRegenerateSingleQr}
              disabled={regeneratingQr}
              className="flex-1 gap-1"
              data-testid="regenerate-single-qr-btn"
            >
              {regeneratingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              إعادة توليد
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clinics;
